import * as React from 'react';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';

import InsightsReportsAnalysisResults from 'modules/insights/reports/components/analysis/results/InsightsReportsAnalysisResults';
import InsightsReportsQueries from 'modules/insights/reports/components/queries/InsightsReportsQueries';
import InsightsReportsCalloutCollabary from 'modules/insights/reports/components/callouts/collabary/InsightsReportsCalloutCollabary';
import InsightsReportsTimeframe from 'modules/insights/reports/components/timeframe/InsightsReportsTimeframe';

import { danger } from 'redux-store/reducers/common-reducer';
import { fetchReport, resetReport, updateReport } from 'modules/insights/reports/reducers/reports/collabary/insights-reports';
import {
  downloadAnalysis,
  resetAnalysis
} from 'modules/insights/reports/reducers/analysis/collabary/insights-reports-analysis';
import {
  createQuery,
  deleteQuery,
  duplicateQuery,
  fetchQueries,
  resetQueries,
  updateQuery
} from 'modules/insights/reports/reducers/queries/collabary/queries/insights-reports-queries';

import { __t, applyTranslations } from 'utils/language';
import { getChannelFromInstagramUrl } from 'utils/channels';
import {
  QUERY,
  REPORT,
  isReadyForAnalysis,
  sanitizeChannelValue
} from 'modules/insights/reports/utils/insights-reports';
import {
  CHANNELS,
  HASHTAGS,
  KPI_DEFAULTS,
  TITLE
} from 'modules/insights/reports/utils/queries/insights-reports-queries';

import type {
  LineGraphEChartsObject,
  LocationWindow
} from 'types';
import type {
  GeneralProps,
  GeneralVoidFunction,
  Query,
  QuerySave,
  Report,
  ReportType,
  ReportUpdateCallback
} from 'modules/insights/reports/types';

applyTranslations(require('modules/insights/reports/shared/insights-reports-shared.i18n.json'));

type OwnProps = {
  location: LocationWindow,
  brandProfileId: number | undefined | null,
  report: Report,
  reportId: number,
  reportType: ReportType,
  graphInstance: LineGraphEChartsObject | undefined | null,
  isReportSaved: boolean,
  isAnalysisDownloading: boolean,
  onRenderBreadcrumbs: () => React.ReactNode,
  onRenderPageHeader: () => React.ReactNode,
  onRenderReportHeader: (onUpdateReport: ReportUpdateCallback, onDownloadReport?: GeneralVoidFunction | null) => React.ReactNode,
  onReportSavedText: () => void,
  onChartInstanceCallback: (echartsInstance: LineGraphEChartsObject) => void
};

type StateProps = {
  queries: Array<Query>,
  hasAnalysisBeenRun: boolean,
  isLoadingQueries: boolean
};

type DispatchProps = {
  dispatchFetchReport: (brandProfileId: number, reportId: number) => Promise<void>,
  dispatchUpdateReport: (report: Report) => Promise<void>,
  dispatchFetchQueries: (reportId: number) => Promise<void>,
  dispatchCreateQuery: (query: Query) => Promise<any>,
  dispatchUpdateQuery: (query: Query) => Promise<any>,
  dispatchDuplicateQuery: (reportId: number, queryId: number) => Promise<any>,
  dispatchDeleteQuery: (reportId: number, queryId: number) => Promise<void>,
  dispatchDownloadAnalysis: (report: Report, chartImage: string, format: string) => void,
  dispatchErrorPopup: (message: string) => void,
  dispatchResetReport: () => void,
  dispatchResetQueries: () => void,
  dispatchResetAnalysis: () => void
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  isActionEvent: boolean,
  isValidForm: boolean
};

const SHARED_MSG_KEY = 'insights_reports_shared';

class InsightsReportsReportPageCollabaryInstagram extends React.Component<Props, State> {
  state = {
    isActionEvent: false,
    isValidForm: true
  };

  componentDidMount() {
    const { brandProfileId, reportId, dispatchFetchReport } = this.props;

    if (brandProfileId && reportId) {
      dispatchFetchReport(brandProfileId, reportId)
        .then(this.handleFetchResidualData);
    }
  }

  componentWillUnmount() {
    const { dispatchResetReport, dispatchResetQueries, dispatchResetAnalysis } = this.props;

    dispatchResetReport();
    dispatchResetQueries();
    dispatchResetAnalysis();
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    const {
      report,
      isReportSaved,
      hasAnalysisBeenRun,
      isAnalysisDownloading,
      graphInstance,
      queries,
      isLoadingQueries
    } = this.props;

    const { isActionEvent, isValidForm } = this.state;

    return !isEqual(report, nextProps.report)
      || !isEqual(queries, nextProps.queries)
      || !isEqual(graphInstance, nextProps.graphInstance)
      || isReportSaved !== nextProps.isReportSaved
      || hasAnalysisBeenRun !== nextProps.hasAnalysisBeenRun
      || isAnalysisDownloading !== nextProps.isAnalysisDownloading
      || isLoadingQueries !== nextProps.isLoadingQueries
      || isActionEvent !== nextState.isActionEvent
      || isValidForm !== nextState.isValidForm;
  }

  setFormStatus() {
    this.setState({
      isValidForm: this.getIsAnalysisFiltersValid()
    });
  }

  getIsAnalysisFiltersValid(): boolean {
    const { report } = this.props;

    return Boolean(isReadyForAnalysis(report) && this.getIsOneQueryEnabledStatus());
  }

  getIsOneQueryEnabledStatus(): boolean {
    const { queries } = this.props;

    return queries.some(item => item.enabled);
  }

  getEndpointData(data: QuerySave): Query {
    const { reportId } = this.props;
    const { query, textFields } = data;

    return {
      name: textFields[TITLE],
      reportId,
      id: query.id,
      channels: textFields[CHANNELS].map(channel => ({
        ...channel,
        username: sanitizeChannelValue(getChannelFromInstagramUrl(channel.username))
      })),
      keywords: textFields[HASHTAGS].map(hashtag => ({
        ...hashtag,
        keyword: hashtag.keyword.replace(/#/, '')
      })),
      estimatedImpressionsMultiplier: (Number(textFields.impressions || KPI_DEFAULTS.impressions)) / 100,
      estimatedReachMultiplier: (Number(textFields.reach || KPI_DEFAULTS.reach)) / 100
    };
  }

  handleFetchResidualData = (): void => {
    const { reportId, dispatchFetchQueries } = this.props;

    if (reportId) {
      dispatchFetchQueries(reportId);
    }
  };

  handleActionEventToggle = (
    status: boolean
  ): void => {
    this.setState({
      isActionEvent: status
    });
  };

  handleDownloadAnalysis = (): void => {
    const { report, graphInstance, dispatchDownloadAnalysis } = this.props;
    const graphImageUrl = graphInstance
      ? graphInstance.getDataURL({ backgroundColor: '#fff' })
      : '';

    dispatchDownloadAnalysis(report, graphImageUrl, 'spreadsheet');
  };

  handleTimeframeChange = (
    startDate: string | null,
    endDate: string | null
  ): void => {
    const { report, dispatchUpdateReport, onReportSavedText } = this.props;

    const updatedReport: Report = {
      ...report,
      startDate,
      endDate
    };

    dispatchUpdateReport(updatedReport)
      .then(onReportSavedText)
      .catch(() => this.handleDispatchErrorPopup('updating', REPORT));
  };

  handleUpdateReport = (
    props: GeneralProps
  ): void => {
    const { report, onReportSavedText, dispatchUpdateReport } = this.props;

    const updatedReport = {
      ...report,
      ...props
    };

    dispatchUpdateReport(updatedReport)
      .then(onReportSavedText)
      .catch(() => this.handleDispatchErrorPopup('updating', REPORT));
  };

  handleDuplicateQuery = (query: Query): void => {
    const { reportId, dispatchDuplicateQuery } = this.props;
    const queryId = query.id;

    if (queryId) {
      this.handleActionEventToggle(true);

      dispatchDuplicateQuery(reportId, queryId)
        .then(() => this.handleActionEventToggle(false))
        .catch(() => this.handleDispatchErrorPopup('duplicating', QUERY));
    }
  };

  handleDeleteQuery = (props: GeneralProps): void => {
    const { dispatchDeleteQuery } = this.props;

    if (props.reportId && props.queryId) {
      this.handleActionEventToggle(true);

      dispatchDeleteQuery(props.reportId, props.queryId)
        .then(() => this.handleActionEventToggle(false))
        .catch(() => this.handleDispatchErrorPopup('deleting', QUERY));
    }
  };

  handleUpdateQuery = (query: Query): void => {
    const { dispatchUpdateQuery } = this.props;

    this.handleActionEventToggle(true);

    dispatchUpdateQuery(query)
      .then(() => this.handleActionEventToggle(false))
      .catch(() => this.handleDispatchErrorPopup('updating', QUERY));
  };

  handleCreateQuery(query: Query): void {
    const { dispatchCreateQuery } = this.props;

    this.handleActionEventToggle(true);

    dispatchCreateQuery(query)
      .then(() => this.handleActionEventToggle(false))
      .catch(() => this.handleDispatchErrorPopup('creating', QUERY));
  }

  handleSaveQuery = (data: QuerySave): void => {
    const { query } = data;
    const endpointData = this.getEndpointData(data);
    const isEditMode = !isEmpty(query);

    const updatedEndpointData = {
      ...endpointData,
      enabled: isEditMode ? Boolean(query.enabled) : true
    };

    // edit mode
    if (isEditMode) {
      this.handleUpdateQuery(updatedEndpointData);
      return;
    }

    // create mode
    this.handleCreateQuery(updatedEndpointData);
  };

  handleDispatchErrorPopup(actionType: string, itemType: string): void {
    const { dispatchErrorPopup } = this.props;

    this.handleActionEventToggle(false);

    dispatchErrorPopup(
      __t(`${SHARED_MSG_KEY}.messages.failure`, {
        actionType: __t(`${SHARED_MSG_KEY}.labels.${actionType}`),
        itemType: __t(`${SHARED_MSG_KEY}.labels.${itemType}`).toLowerCase()
      })
    );
  }

  renderTimeframe(): React.ReactNode {
    const { report } = this.props;
    const { isValidForm } = this.state;

    return (
      <div className="insights-reports-report-page-collabary__timeframe">
        <InsightsReportsTimeframe
          startDate={report.startDate}
          endDate={report.endDate}
          withErrorHighlight={!isValidForm}
          onChange={this.handleTimeframeChange}
        />
      </div>
    );
  }

  renderCallout(): React.ReactNode | undefined | null {
    const { report, reportType, queries, isLoadingQueries } = this.props;

    if (isLoadingQueries) {
      return null;
    }

    if (isEmpty(queries)) {
      return (
        <InsightsReportsCalloutCollabary
          report={report}
          reportType={reportType}
          onSaveQuery={this.handleSaveQuery}
        />
      );
    }

    return null;
  }

  renderQueries(): React.ReactNode {
    const { report } = this.props;
    const { isActionEvent, isValidForm } = this.state;

    return (
      <InsightsReportsQueries
        report={report}
        isInvalidQueriesSelection={!isValidForm && !this.getIsOneQueryEnabledStatus()}
        isActionEvent={isActionEvent}
        onSaveQuery={this.handleSaveQuery}
        onUpdateQuery={this.handleUpdateQuery}
        onDuplicateQuery={this.handleDuplicateQuery}
        onDeleteQuery={this.handleDeleteQuery}
      />
    );
  }

  renderAnalysis(): React.ReactNode {
    const { location, reportType, onChartInstanceCallback } = this.props;

    return (
      <InsightsReportsAnalysisResults
        reportType={reportType}
        location={location}
        isValidSetup={this.getIsAnalysisFiltersValid()}
        chartInstanceCallback={onChartInstanceCallback}
        onFormStatusCheck={() => this.setFormStatus()}
      />
    );
  }

  renderReport(): React.ReactNode | undefined | null {
    const { queries } = this.props;

    return !isEmpty(queries) && (
      <React.Fragment>
        {this.renderQueries()}
        {this.renderTimeframe()}
        {this.renderAnalysis()}
      </React.Fragment>
    );
  }

  render(): React.ReactNode {
    const { onRenderBreadcrumbs, onRenderPageHeader, onRenderReportHeader } = this.props;

    return (
      <div className="insights-reports-report-page-collabary insights-reports-report-page-collabary-instagram">
        {onRenderBreadcrumbs()}
        {onRenderPageHeader()}
        {onRenderReportHeader(this.handleUpdateReport, this.handleDownloadAnalysis)}
        {this.renderReport()}
        {this.renderCallout()}
      </div>
    );
  }
}

const mapStateToProps = ({
  insightsReportsQueries,
  insightsReportsAnalysis
}): StateProps => ({
  queries: insightsReportsQueries.queries,
  hasAnalysisBeenRun: insightsReportsAnalysis.analysisHasBeenRun,
  isLoadingQueries: insightsReportsQueries.isLoading
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchReport: (brandProfileId: number, reportId: number) => (
    dispatch(fetchReport(brandProfileId, reportId))
  ),
  dispatchUpdateReport: (report: Report) => (
    dispatch(updateReport(report))
  ),
  dispatchFetchQueries: (reportId: number) => (
    dispatch(fetchQueries(reportId))
  ),
  dispatchCreateQuery: (query: Query) => (
    dispatch(createQuery(query))
  ),
  dispatchUpdateQuery: (query: Query) => (
    dispatch(updateQuery(query))
  ),
  dispatchDuplicateQuery: (reportId: number, queryId: number) => (
    dispatch(duplicateQuery(reportId, queryId))
  ),
  dispatchDeleteQuery: (reportId: number, queryId: number) => (
    dispatch(deleteQuery(reportId, queryId))
  ),
  dispatchDownloadAnalysis: (report: Report, chartImage: string, format: string) => (
    dispatch(downloadAnalysis(report, chartImage, format))
  ),
  dispatchErrorPopup: (message: string) => (
    dispatch(danger(message))
  ),
  dispatchResetReport: () => (
    dispatch(resetReport())
  ),
  dispatchResetQueries: () => (
    dispatch(resetQueries())
  ),
  dispatchResetAnalysis: () => (
    dispatch(resetAnalysis())
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(InsightsReportsReportPageCollabaryInstagram);
